<template>
  <v-container fluid>
    <v-row>

      <v-col cols="12" lg="6">
        <v-text-field
          class="t-search-query"
          :label="$t('rights.search')"
          v-model="searchQuery"
        >
          <v-icon slot="append" color="grey">mdi-magnify</v-icon>
        </v-text-field>
      </v-col>

      <v-col cols="12" lg="6">
        <v-toolbar class="shrink-bar float-right" dense elevation="0">
          <v-menu :close-on-content-click="false">
            <template v-slot:activator="menuScope">
              <v-tooltip top>
                <template v-slot:activator="tooltipScope">
                  <v-btn v-on="{ ...menuScope.on, ...tooltipScope.on }" :small="$vuetify.breakpoint.smAndDown" class="mx-1 my-2">
                    <v-icon left>mdi-view-column</v-icon>
                    <span>{{ $vuetify.breakpoint.mdAndDown ? $t('rights.columnsButtonShort') : $t('rights.columnsButton') }}</span>
                  </v-btn>
                </template>
                <span>{{ $t('rights.columnsButtonTooltip') }}</span>
              </v-tooltip>
            </template>

            <v-list dense>
              <v-list-item v-for="col in additionalColumns" :key="col.value">
                <v-list-item-action>
                  <v-checkbox
                    :value="selectedColumns.includes(col.value)"
                    @change="toggleColumnSelect(col.value)"
                  ></v-checkbox>
                </v-list-item-action>
                <v-list-item-content>
                  {{ col.text }}
                </v-list-item-content>
              </v-list-item>
            </v-list>
          </v-menu>

          <v-tooltip top>
            <template v-slot:activator="tooltipScope">
              <v-btn v-on="tooltipScope.on" :disabled="downloading" :small="$vuetify.breakpoint.smAndDown" class="mx-1 my-2" color="primary" @click="downloadRights">
                <v-icon v-if="downloading" left>mdi-loading mdi-spin</v-icon>
                <v-icon v-else left>mdi-download</v-icon>
                <span>{{ $vuetify.breakpoint.mdAndDown ? $t('rights.downloadShort') : $t('rights.download') }}</span>
              </v-btn>
            </template>
            <span>{{ $t('rights.downloadTooltip') }}</span>
          </v-tooltip>

          <v-tooltip top>
            <template v-slot:activator="tooltipScope">
              <v-btn v-on="tooltipScope.on" :small="$vuetify.breakpoint.smAndDown" class="mx-1 my-2" color="green" :disabled="checkedItems.length === 0" @click="onAddRights">
                <v-icon color="white">mdi-plus</v-icon>
                <span class="text--white">{{ $t('rights.add') }} </span>
              </v-btn>
            </template>
            <span>{{ $t('rights.add') }}</span>
          </v-tooltip>

        </v-toolbar>
      </v-col>

    </v-row>

    <RightsTable
      :items="rightsList"
      :checked-items.sync="checkedItems"
      :displayed-columns="displayedColumns"
      :continuos-amount="!unclaimedRights.finished"
      :portion-loading="portionLoading"
      :total-count="unclaimedRights.totalCount"
      @load-more="loadMoreItems"
    >

    </RightsTable>

  </v-container>
</template>

<script>
import RightsTable from "@/components/rights/RightsTable";
import {mapGetters} from "vuex";
import DebounceSearchLauncherMixin from "@/components/mixins/DebounceSearchLauncherMixin";
import RightsholderInitedDataDrivenMixin from "@/components/mixins/RightsholderInitedDataDrivenMixin";
import downloadFile from "@/utils/downloadFile";

export default {
  name: 'UnclaimedRights',
  components: {RightsTable},
  data() {
    return {
      selectedColumns: [],
      portionLoading: false,
      addRightVisible: false,
      checkedItems: [],
      downloading: false
    }
  },

  mixins: [DebounceSearchLauncherMixin, RightsholderInitedDataDrivenMixin],

  computed: {
    ...mapGetters('app', ['unclaimedRights']),

    rightsList() {
      if (!this.unclaimedRights.list) {
        return [];
      }
      return this.unclaimedRights.list.concat(this.unclaimedRights.finished ? [] : [null]);
    },

    additionalColumns() {
      const getCol = (value) => ({value, text: this.$t(`rights.columns.${value}`)})
      return ['rowNumber', 'spnr', 'version', 'year', 'labelName'].map(getCol);
    },

    allColumns() {
      return [
        'rowNumber',
        'type',
        'isrc',
        'spnr',
        'artistName',
        'title',
        'countryCode',
        'year',
        'labelName',
        'status',
        'version',
        'added'
      ];
    },

    displayedColumns() {
      return this.allColumns.filter((col) => (this.additionalColumns.some(({value}) => value === col) ? this.selectedColumns.includes(col) : true));
    }
  },

  methods: {

    // Override
    reloadItems() {
      this.loadRights();
    },

    // Override
    searchQueryUpdated() {
      this.loadRights();
    },

    loadRights() {
      if (!this.portionLoading === true) {
        this.portionLoading = true;
        this.$store.dispatch('app/loadUnclaimedRightsList', {reload: true, search: this.searchQuery})
          .catch((err) => {
            this.$toast(err.message);
          })
          .finally(() => {
            this.portionLoading = false;
          });
      }
      return Promise.resolve('Already Loading')

    },

    loadMoreItems() {
      this.portionLoading = true;
      this.$store.dispatch('app/loadUnclaimedRightsList', {search: this.searchQuery})
        .catch((err) => {
          this.$toast(err.message);
        })
        .finally(() => {
          this.portionLoading = false;
        })
    },

    toggleColumnSelect(col) {
      if (this.selectedColumns.includes(col)) {
        this.selectedColumns = this.selectedColumns.filter(c => c !== col);
      } else {
        this.selectedColumns.push(col);
      }
    },

    downloadRights() {
      this.downloading = true
      this.$store.dispatch('app/getUnclaimedRightsDownloadUrl', {search: this.searchQuery, format: 'xlsx'})
        .then((res) => {
          this.downloading = false
          downloadFile((res.filename ? res.filename : 'rights.xlsx'), res.url);
        })
    },

    onAddRights() {
      this.$store.dispatch('app/addRightsToStaging', this.checkedItems)
        .then(() => {
          this.$toast(this.$t('rights.added_to_staging'));
          this.checkedItems = [];
        });
    }
  }
}
</script>